"use client"

import React from "react"
import { useState } from "react"
import { Card, Typography, Tag, Button, Spin, Empty, Alert } from "antd"
import { EyeOutlined, TrophyOutlined, ClockCircleOutlined, BookOutlined, BulbOutlined } from "@ant-design/icons"
import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"

// Import the new components
import ScoreDrawer from "./ScoreDrawer"
import { fetchReports } from "../../../actions"
import { selectReports, selectReportsLoading, selectReportsError } from "../../../selectors"

const { Title, Text } = Typography

// Styled Components with consistent design
const TabContent = styled.div`
  padding: 24px;
  min-height: 500px;
`

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`

const MetricCard = styled(Card)`
  border-radius: 8px;
  text-align: center;
  background: white;
  border: 1px solid #f0f0f0;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 20px 16px;
  }
`

const MetricIcon = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
  color: ${(props) => props.color || "#3249D7"};
`

const MetricValue = styled(Title)`
  margin: 0 !important;
  font-size: 20px !important;
  font-weight: 600 !important;
  color: #333333 !important;
`

const MetricLabel = styled(Text)`
  font-size: 12px;
  color: #666666;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const HistorySection = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

const HistoryHeader = styled.div`
  padding: 16px 20px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
`

const HistoryTitle = styled(Title)`
  margin: 0 !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #333333 !important;
`

const AssessmentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  transition: background-color 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafafa;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const AssessmentInfo = styled.div`
  flex: 1;
`

const AssessmentTitle = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  color: #262626;
  display: block;
  margin-bottom: 4px;
`

const AssessmentMeta = styled(Text)`
  font-size: 12px;
  color: #8c8c8c;
  display: block;
  margin-bottom: 2px;
`

const AssessmentActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px;
  background: white;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  flex-direction: column;
  gap: 16px;
`

const EmptyContainer = styled.div`
  padding: 60px 20px;
  text-align: center;
  background: white;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`

const ErrorContainer = styled.div`
  margin-bottom: 16px;
`

const AssessmentTab = ({
  assessmentMetrics,
  studentAssessmentHistory,
  studentDataLoading,
  handleViewDetails,
  student,
}) => {
  const dispatch = useDispatch()

  const [scoreDrawerVisible, setScoreDrawerVisible] = useState(false)
  const [selectedAssessment, setSelectedAssessment] = useState(null)
  const [error, setError] = useState(null)

  const reports = useSelector(selectReports)
  const reportsLoading = useSelector(selectReportsLoading)
  const reportsError = useSelector(selectReportsError)

  const handleViewDetailsClick = async (assessment) => {
    try {
      setError(null)
      setSelectedAssessment(assessment)

      const studentId = student?.studentNumber || student?.id || student?.student_id

      if (!studentId) {
        throw new Error("Student ID not found")
      }

      await dispatch(fetchReports([assessment.assessmentAssignedId || assessment.assessment_assigned_id], studentId))
      setScoreDrawerVisible(true)
    } catch (error) {
      console.error("Error fetching assessment report:", error)
      setError("Failed to load assessment details. Please try again.")
    }
  }

  const handleScoreDrawerClose = () => {
    setScoreDrawerVisible(false)
    setSelectedAssessment(null)
    setError(null)
  }

  const getMetricColor = (type) => {
    switch (type) {
      case "attempt":
        return "#3249D7"
      case "communication":
        return "#0FB680"
      case "aptitude":
        return "#faad14"
      case "technical":
        return "#722ed1"
      default:
        return "#3249D7"
    }
  }

  const calculateAptitudeCompleted = () => {
    if (!studentAssessmentHistory || studentAssessmentHistory.length === 0) return 0
    
    console.log("Calculating Aptitude Completed from history:", studentAssessmentHistory);
    console.log("Total Assessments:", studentAssessmentHistory.length);

    return studentAssessmentHistory.filter(assessment => {
      const type = (assessment.type || "").toLowerCase()
      return type === "aptitude" && assessment.status === "completed"
    }).length
  }

  const formatAttemptRate = () => {
    console.log("Assessment Metrics:", assessmentMetrics);
    console.log("Calculated Aptitude Completed:", calculateAptitudeCompleted());
    
    if (assessmentMetrics.totalSent === 0) return "N/A"
    return `${assessmentMetrics.attemptRate}%`
  }

  return (
    <TabContent>
      {error && (
        <ErrorContainer>
          <Alert message="Error" description={error} type="error" closable onClose={() => setError(null)} />
        </ErrorContainer>
      )}

      <MetricsGrid>
        <MetricCard>
          <MetricIcon color={getMetricColor("attempt")}>
            <TrophyOutlined />
          </MetricIcon>
          <MetricValue>{formatAttemptRate()}</MetricValue>
          <MetricLabel>Attempt Rate</MetricLabel>
        </MetricCard>

        <MetricCard>
          <MetricIcon color={getMetricColor("communication")}>
            <BookOutlined />
          </MetricIcon>
          <MetricValue>
            {assessmentMetrics.communicationCompleted}/{assessmentMetrics.totalSent}
          </MetricValue>
          <MetricLabel>Communication</MetricLabel>
        </MetricCard>

        <MetricCard>
          <MetricIcon color={getMetricColor("aptitude")}>
            <BulbOutlined />
          </MetricIcon>
          <MetricValue>{calculateAptitudeCompleted()}/{studentAssessmentHistory.length}</MetricValue>
          <MetricLabel>Aptitude</MetricLabel>
        </MetricCard>

        {/* <MetricCard>
          <MetricIcon color={getMetricColor("technical")}>
            <ClockCircleOutlined />
          </MetricIcon>
          <MetricValue>{assessmentMetrics.technicalCompleted || 0}</MetricValue>
          <MetricLabel>Technical</MetricLabel>
        </MetricCard> */}
      </MetricsGrid>

      <HistorySection>
        <HistoryHeader>
          <HistoryTitle>Assessment History</HistoryTitle>
        </HistoryHeader>

        {studentDataLoading ? (
          <LoadingContainer>
            <Spin size="large" />
            <div style={{ marginLeft: "12px", color: "#8c8c8c" }}>Loading assessment history...</div>
          </LoadingContainer>
        ) : studentAssessmentHistory.length > 0 ? (
          <div>
            {studentAssessmentHistory.map((assessment, index) => (
              <AssessmentItem key={assessment.assessment_assigned_id || index}>
                <AssessmentInfo>
                  <AssessmentTitle>
                    {assessment.title} ({assessment.type})
                  </AssessmentTitle>
                  <AssessmentMeta>Started: {assessment.sentDate}</AssessmentMeta>
                  {assessment.endDate && assessment.endDate !== "-" && (
                    <AssessmentMeta>Due: {assessment.endDate}</AssessmentMeta>
                  )}
                  {assessment.submittedAt && (
                    <AssessmentMeta style={{ color: "#52c41a" }}>
                      Completed: {new Date(assessment.submittedAt).toLocaleDateString()}
                    </AssessmentMeta>
                  )}
                </AssessmentInfo>

                <AssessmentActions>
                  <Tag
                    color={assessment.statusColor}
                    style={{
                      fontSize: "11px",
                      fontWeight: "500",
                      border: "none",
                      borderRadius: "4px",
                    }}
                  >
                    {assessment.statusText}
                  </Tag>

                  {assessment.status === "completed" ? (
                    <Button
                      size="small"
                      type="primary"
                      icon={<EyeOutlined />}
                      onClick={() => handleViewDetailsClick(assessment)}
                      loading={
                        reportsLoading &&
                        selectedAssessment?.assessment_assigned_id === assessment.assessment_assigned_id
                      }
                      style={{
                        borderRadius: "4px",
                        fontSize: "12px",
                        height: "28px",
                      }}
                    >
                      View Details
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      disabled
                      style={{
                        borderRadius: "4px",
                        fontSize: "12px",
                        height: "28px",
                      }}
                    >
                      {assessment.status === "pending" ? "Pending" : "Not Attempted"}
                    </Button>
                  )}
                </AssessmentActions>
              </AssessmentItem>
            ))}
          </div>
        ) : (
          <EmptyContainer>
            <Empty
              description="No assessment history available for this student"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </EmptyContainer>
        )}
      </HistorySection>

      <ScoreDrawer
        visible={scoreDrawerVisible}
        onClose={handleScoreDrawerClose}
        assessment={selectedAssessment}
        reports={reports}
        loading={reportsLoading}
        error={reportsError}
        onDownloadReport={() => {}}
        onViewDetailedReport={() => {}}
      />
    </TabContent>
  )
}

export default AssessmentTab
